import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Translator from "services/Translator";
import supportApi from "support/api/supportApi";
import { getCachedConfig, setCachedConfig } from "support/storage/supportStorage";
import { setConfig } from "stores/support/supportSlice";
import { selectSupportConfig } from "stores/support/supportSelectors";

// widget-config is the whole messenger bootstrap: departments, forms, copy,
// feature flags and the transport ladder. It is fetched once per locale on the
// first panel open, served from a short localStorage TTL after that.

const TTL_MS = 10 * 60 * 1000;

// The backend hands back an empty PHP array rather than an empty object when
// nothing is published, so forms and the string blocks can arrive as [].
function asObject(value) {
  return value && !Array.isArray(value) && typeof value === "object" ? value : {};
}

function normalize(data) {
  const raw = data || {};
  const theme = asObject(raw.theme);
  const strings = asObject(raw.strings);
  const transport = asObject(raw.transport);
  return {
    brand: raw.brand || null,
    direction: raw.direction === "rtl" ? "rtl" : "ltr",
    theme: {
      css_vars: asObject(theme.css_vars),
      position: theme.position || "bottom-right",
      logo_asset_id: theme.logo_asset_id || null,
      avatar_asset_id: theme.avatar_asset_id || null,
    },
    launcher: asObject(raw.launcher),
    departments: Array.isArray(raw.departments) ? raw.departments : [],
    forms: asObject(raw.forms),
    business_hours: {
      spec: asObject(raw.business_hours).spec || null,
      online: !!asObject(raw.business_hours).online,
      next_open_at: asObject(raw.business_hours).next_open_at || null,
    },
    announcements: Array.isArray(raw.announcements) ? raw.announcements : [],
    quick_replies: Array.isArray(raw.quick_replies) ? raw.quick_replies : [],
    features: asObject(raw.features),
    strings: {
      ui: asObject(strings.ui),
      consent: asObject(strings.consent),
      rg: asObject(strings.rg),
    },
    transport: {
      ws_enabled: !!transport.ws_enabled,
      ws_host: transport.ws_host || null,
      ws_key: transport.ws_key || null,
      poll_interval_ms: parseInt(transport.poll_interval_ms, 10) || 12000,
    },
    config_versions: asObject(raw.config_versions),
  };
}

export function currentLocale() {
  const language = Translator.getSelectedLanguage();
  return (language && language.code) || "en";
}

// Server-authored theming: the published widget_theme writes --sup-* onto the
// same :root the stylesheet defaults live on, so brand changes need no deploy.
export function applyThemeVars(config) {
  const vars = config && config.theme ? asObject(config.theme.css_vars) : {};
  const dark = asObject(vars.dark);
  const root = document.documentElement;
  Object.keys(dark).forEach((key) => {
    const name = key.indexOf("--") === 0 ? key : "--sup-" + key;
    root.style.setProperty(name, dark[key]);
  });
}

export default function useSupportConfig(active) {
  const dispatch = useDispatch();
  const config = useSelector(selectSupportConfig);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (force) => {
      const locale = currentLocale();
      if (!force) {
        const cached = getCachedConfig(locale);
        if (cached) {
          dispatch(setConfig(cached));
          applyThemeVars(cached);
          return cached;
        }
      }
      setLoading(true);
      setError(null);
      const response = await supportApi.getWidgetConfig(locale);
      setLoading(false);
      if (response.status !== 200) {
        setError(response.status);
        return null;
      }
      const normalized = normalize(response.data);
      setCachedConfig(locale, normalized, TTL_MS);
      dispatch(setConfig(normalized));
      applyThemeVars(normalized);
      return normalized;
    },
    [dispatch]
  );

  useEffect(() => {
    if (active && !config && !loading) {
      load(false);
    }
  }, [active, config, loading, load]);

  return { config, loading, error, reload: () => load(true) };
}
